import { ITagDriverMapping } from "@app/interfaces/ITag";
import { ICombinedPreferences } from "@interfaces/ICombinedPreferences";
import { IDateParameters } from "@interfaces/IDateParameters";
import { IDriver } from "@interfaces/IDriver";
import { TranslationService } from "@services/translation.service";
import * as angular from "angular";
import "angular-material";
import * as moment from "moment";
import { SettingsName } from "../class/SettingsName";
import { CacheService } from "./cache.Service";
import { ConstantsService } from "./constantsService";
import { DisplayService } from "./display.service";
import { SessionService } from "./session.service";
import { IDriverTachoHours } from "./tacho.service";
import { TagResourceType, TagsService } from "./tags.service";

export interface IGetDriverOptions {
    includeTags?: boolean;

    invalidateCache?: boolean;
}

export class DriverService {
    userOptions: ICombinedPreferences;
    isDemoSite: boolean;
    private cacheName = "driverCache";

    static $inject = ["$q", "$http", "cacheService", "tagsService", "sessionService", "displayService", "constantsService", "translationService"];

    constructor(
        private readonly $q: angular.IQService,
        private readonly $http: angular.IHttpService,
        private readonly cacheService: CacheService,
        private readonly tagsService: TagsService,
        private readonly sessionService: SessionService,
        private readonly displayService: DisplayService,
        private readonly constantsService: ConstantsService,
        private readonly translationService: TranslationService) {

        this.isDemoSite = this.constantsService.getIsDemoSite();
        this.cacheService.createCache(this.cacheName, { maxAge: 30 * 60 * 1000 });
    }

    private getDateRange(
        startDate: Date | moment.Moment,
        endDate?: Date | moment.Moment
    ): IDateParameters {
        const startMoment = moment(startDate).startOf("day");
        const endMoment = moment(endDate || startDate).startOf("day");

        endMoment.add(1, "day");

        const params: IDateParameters = {
            start: startMoment.utc().format(),
            end: endMoment.utc().format(),
        };

        return params;
    }

    getCachedDriver(driverId: number): IDriver {
        var cachedDrivers = this.cacheService.get<IDriver[]>(this.cacheName);

        if (cachedDrivers == null || cachedDrivers.length == 0) {
            console.warn("No cached drivers", driverId);
            return null;
        }

        for (let i = 0; i < cachedDrivers.length; i++) {
            const driver = cachedDrivers[i];

            if (driverId === driver.Id) {
                return driver;
            }
        }

        console.warn("Couldnt find driver", driverId);

        return null;
    }

    getDrivers(options?: IGetDriverOptions): angular.IPromise<IDriver[]> {
        this.getUserOptions();

        const driversPromise = this.cacheService.getOrRetrieveData<IDriver[]>(this.cacheName, () => this.getDriversFromDatabase(), options?.invalidateCache).then((drivers) => {
            return this.labelInactive(drivers);
        });

        const tagMappingPromise = options?.includeTags ? this.tagsService.getTagDriverMappings({}, options?.invalidateCache) : this.$q.when<ITagDriverMapping[]>([]);

        return this.$q
            .all([tagMappingPromise, driversPromise])
            .then(([tagMappings, drivers]) => {

                this.tagsService.mapTagMappingsToResources(tagMappings, drivers, TagResourceType.Driver);

                return drivers;
            });
    }

    private labelInactive(drivers: IDriver[]): IDriver[] {
        if (this.userOptions?.ShowInactiveDrivers?.Value) {
            drivers.forEach((driver) => {
                driver.DisplayName =
                    driver.Status === 0
                        ? this.translationService.t('driverService.inactiveDriver', driver.Name)
                        : driver.Name;
            });

            return drivers;
        }
        else {
            const activeDriverList: IDriver[] = [];

            for (let i = 0; i < drivers.length; i++) {
                const driver = drivers[i];

                // If driver is active include it
                if (driver.Status === 1) {
                    driver.DisplayName = driver.Name;
                    activeDriverList.push(driver);
                }
            }

            return activeDriverList;
        }
    }

    getDriver(id: number, options?: IGetDriverOptions): angular.IPromise<IDriver> {

        const driverPromise = this.$http({
            url: `/api/drivers/${id}`,
            method: "GET",
        }).then((response: angular.IHttpPromiseCallbackArg<IDriver>) => {
            return response.data;
        });

        const tagMappingPromise = options?.includeTags ? this.tagsService.getTagDriverMappings({ DriverId: id }, options?.invalidateCache) : this.$q.when<ITagDriverMapping[]>([]);

        return this.$q
            .all([tagMappingPromise, driverPromise])
            .then(([tagMappings, driver]) => {

                this.tagsService.mapTagMappingsToResources(tagMappings, [driver], TagResourceType.Driver);

                return driver;
            });
    }

    // Todo: Move to tacho service?
    getDriversTacho(): angular.IPromise<IDriverTachoHours[]> {
        return this.$http({
            url: "/api/drivers/tacho",
            method: "GET",
            cache: true,
            params: { minsToLive: 5 }
        }).then((response: angular.IHttpPromiseCallbackArg<IDriverTachoHours[]>) => {
            return response.data;
        });
    }

    // Todo: Move to tacho service?
    getCurrentTachoForDriver(
        driverId: number
    ): angular.IPromise<IDriverTachoHours> {
        if (!driverId) {
            //driverId = -1;
            return this.$q.reject();
        }

        return this.$http
            .get(`/api/drivers/${driverId}/tacho/latest`)
            .then(
                (
                    response: angular.IHttpPromiseCallbackArg<IDriverTachoHours>
                ) => {
                    return response.data;
                }
            );
    }

    findDriver(drivers: IDriver[], driverId: number): IDriver {
        for (let driverIndex = 0; driverIndex < drivers.length; driverIndex++) {
            const driver = drivers[driverIndex];

            if (driver.Id === driverId) {
                return driver;
            }
        }

        return null;
    }

    findDriverByName(drivers: IDriver[], driverToFind: string): IDriver {
        const lowerCaseName = driverToFind.toLowerCase().trim();

        for (let i = 0; i < drivers.length; i++) {
            const driver = drivers[i];

            if (driver.DisplayName.toLowerCase().trim() === lowerCaseName) {
                return driver;
            }
        }

        return null;
    }

    driverSearchPredicate(driver: IDriver, searchString: string): boolean {
        return driver.DisplayName.toLowerCase().includes(searchString.toLowerCase());
    }

    searchDrivers(drivers: IDriver[], text: string) {
        return drivers.filter(v => this.driverSearchPredicate(v, text));
    }

    updateDriver(driver: IDriver, invalidateCache = false) {
        driver.FirstName = null;
        driver.LastName = null;

        return this.$http({
            url: `/api/drivers/`,
            method: "POST",
            data: driver,
            params: { invalidateCache }
        }).then((result: angular.IHttpPromiseCallbackArg<number>) => {

            if ((!driver.Id || driver.Id === 0) && result.data) {
                driver.Id = result.data;
            }

            return this.tagsService.saveResourceTags(driver, TagResourceType.Driver).then(() => {

                this.updateOrInsertDriverCache(driver);

                return driver.Id;
            });
        });
    }

    updateOrInsertDriverCache(driver: IDriver): void {
        this.cacheService.addOrUpdateItem(this.cacheName, driver, (v1, v2) => v1.Id == v2.Id);
    }

    private getDriverCacheIndex(
        driver: IDriver,
        driversCache: IDriver[]
    ): number {
        for (let i = 0; i < driversCache.length; i++) {
            if (driversCache[i].Id === driver.Id) {
                return i;
            }
        }
        return -1;
    }

    isDriverUsernameInUse(username: string) {
        const promise = this.$http({
            url: `/api/drivers/isDriverUsernameInUse`,
            method: "GET",
            params: { username: username },
        }).then((result: angular.IHttpPromiseCallbackArg<boolean>) => {
            return result.data;
        });

        return promise;
    }

    deactivateDriver(driver: IDriver) {
        const promise = this.$http({
            url: `/api/drivers/${driver.Id}`,
            method: "DELETE",
        });

        this.updateOrInsertDriverCache({ ...driver, Status: 0 });

        return promise;
    }

    reactivateDriver(driver: IDriver) {
        driver.Status = 1;

        const promise = this.$http({
            method: "POST",
            url: "/api/drivers",
            data: driver,
        }).then((result: angular.IHttpPromiseCallbackArg<number>) => {

            this.updateOrInsertDriverCache(driver);

            return result.data;
        });

        return promise;
    }

    getDriverImage(driver: IDriver): angular.IPromise<string> {
        const promise = this.$http({
            url: `/api/drivers/${driver.Id}/image`,
            method: "Get",
        }).then((result: angular.IHttpPromiseCallbackArg<string>) => {
            return result.data;
        });

        return promise;
    }

    saveDriverImage(
        driverId: number,
        base64ImageString: string
    ): angular.IPromise<any> {
        return this.$http({
            url: `/api/drivers/${driverId}/image`,
            method: "POST",
            data: base64ImageString ? `"${base64ImageString}"` : null,
        });
    }

    // ---------------------------------------------------

    private getDriversFromDatabase(): angular.IPromise<IDriver[]> {
        return this.$http({
            url: "/api/drivers",
            method: "GET",
        }).then((response: angular.IHttpPromiseCallbackArg<IDriver[]>) => {
            // Filter out blank phone numbers
            response.data.forEach((driver, i) => {
                if (
                    driver.Mobile != null &&
                    driver.Mobile.match(/^ *$/) !== null
                ) {
                    driver.Mobile = null;
                }

                if (this.isDemoSite) {
                    this.censorDriverName(driver, i + 1);
                }
            });

            this.cacheService.save(this.cacheName, response.data);

            return response.data;
        });
    }

    private censorDriverName(driver: IDriver, idx: number) {
        driver.Name = this.translationService.t('driverName', idx);
    }

    private getUserOptions() {
        if (this.userOptions) {
            return;
        }

        this.userOptions = this.sessionService.getItem<ICombinedPreferences>(
            SettingsName.UserOptions
        );
    }
}
